import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../utils/api';
import { toEasternDateShort } from '../utils/time';
import { useListFilter } from '../hooks/useListFilter';
import Tabs from '../components/Tabs';
import { PageHeader, StatCard, Panel, StatusPill, Empty, Skeleton, Icon, SortTh, useSort, usePager, Pager, useToast } from '../../components/ui';

const TABS = [['all', 'All escalations'], ['open', 'Open'], ['resolved', 'Resolved']];
const SOURCE_LABELS = { comment: 'Comment', message: 'Message', lead: 'Lead', review: 'Review' };
// Categories that need a person right away are red; money and service complaints are amber.
const CAT_TONES = { MEDICAL: 'red', LEGAL: 'red', ABUSE: 'red', PHI: 'red', CRISIS: 'red', COMPLAINT: 'amber', PAYMENT: 'amber', INSURANCE: 'amber' };

const statusOf = (e) => (e.resolved === 'true' ? 'resolved' : 'open');
const searchFields = (e) => [e.author_name, e.content_summary, e.classification, e.source_type];

export default function EscalationsPage() {
  const [showToast, toastNode] = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [tab, setTab] = useState('all');
  const [q, setQ] = useState('');
  const { sort, onSort, apply } = useSort('timestamp', 'desc');

  const load = useCallback(() =>
    api.getEscalations()
      .then(r => { setItems(r.items || []); setLastUpdated(new Date()); })
      .catch(() => showToast('Could not load escalations', 'error'))
      .finally(() => setLoading(false)), [showToast]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (id) => {
    try {
      await api.resolveEscalation(id);
      setItems(prev => prev.map(x => x.escalation_id === id ? { ...x, resolved: 'true' } : x));
      showToast('Marked as resolved');
    } catch (e) {
      showToast(e.message || 'Could not resolve the escalation', 'error');
    }
  };

  const { counts, filtered } = useListFilter(items, { statusOf, searchFields, tab, q });
  const sorted = apply(filtered, (e, col) => (col === 'status' ? statusOf(e) : e[col] || ''));
  const { pageRows, pagerProps } = usePager(sorted, [tab, q, sort.col, sort.dir]);

  return (
    <>
      <PageHeader
        title="Escalations"
        subtitle="Sensitive comments and messages the agent handed to a person instead of replying."
        meta={lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : null}
        actions={<button type="button" className="ops-btn" onClick={load} title="Refresh"><Icon name="refresh" /> Refresh</button>}
      />

      {!loading && items.length > 0 && (
        <div className="ops-kpis">
          <StatCard label="Total escalations" value={counts.all} />
          <StatCard label="Open" value={counts.open || 0} tone={counts.open > 0 ? 'red' : undefined} />
          <StatCard label="Resolved" value={counts.resolved || 0} tone="green" />
        </div>
      )}

      <Panel flush
        title={<Tabs tabs={TABS} active={tab} counts={counts} onChange={setTab} />}
        action={<div className="ops-search"><Icon name="search" /><input className="ops-input" value={q} onChange={e => setQ(e.target.value)} placeholder="Search escalations" aria-label="Search escalations" /></div>}
        foot={!loading && sorted.length > 0 ? <Pager {...pagerProps} label="escalations" /> : null}>
        {loading ? <Skeleton rows={6} />
          : items.length === 0 ? <Empty title="No escalations">Nothing has needed a person's attention.</Empty>
            : sorted.length === 0 ? <Empty title="No matching escalations">Nothing matches this search or filter.</Empty> : (
              <div className="ops-table-wrap">
                <table className="ops-table">
                  <thead>
                    <tr>
                      <SortTh col="timestamp" label="Date" sort={sort} onSort={onSort} />
                      <th>Source</th>
                      <SortTh col="classification" label="Category" sort={sort} onSort={onSort} />
                      <th>From</th>
                      <th>Summary</th>
                      <SortTh col="status" label="Status" sort={sort} onSort={onSort} />
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map(e => {
                      const open = e.resolved !== 'true';
                      return (
                        <tr key={e.escalation_id}>
                          <td className="mono muted">{toEasternDateShort(e.timestamp)}</td>
                          <td><StatusPill status={e.source_type} label={SOURCE_LABELS[e.source_type] || e.source_type} /></td>
                          <td><span className={`ops-pill ops-pill--${CAT_TONES[e.classification] || 'neutral'}`}>{e.classification}</span></td>
                          <td className="ops-cell-main">{e.author_name || 'Unknown'}</td>
                          <td className="muted" style={{ maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.content_summary}>{e.content_summary}</td>
                          <td><StatusPill status={open ? 'open' : 'resolved'} /></td>
                          <td style={{ textAlign: 'right' }}>
                            {open && <button type="button" className="ops-btn ops-btn--sm" onClick={() => resolve(e.escalation_id)}><Icon name="check" /> Resolve</button>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
      </Panel>
      {toastNode}
    </>
  );
}
